import { Fragment, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import OnboardCpaSteps from "../../ui/steps/OnboardCpaSteps";
import { updateUserStatus } from "../../../store/redux_auth/authSlice";
import { loadStripe } from "@stripe/stripe-js";
import {
  createIdentityVerificationSession,
  checkVerificationStatus,
} from "../../../api/account/identityVerification";
import IdentityVerificationNotificationService from "../../../services/sockets/notifications/IdentityVerificationNotificationService";
import LoadingPlaceholder from "../../../components/ui/loading/LoadingPlaceholder";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function OnboardingLegalV2() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const cpa_status = user?.account_status;
  const role = user?.user_role;
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Handle Stripe Identity verification
  const handleVerifyIdentity = async () => {
    try {
      const url = "account/create-identity-verification-session";
      const res = await createIdentityVerificationSession(url);
      const data = await res.json();
      if (data.error) {
        return;
      }

      // Store the session ID for polling
      if (data.session_id) {
        setSessionId(data.session_id);
      }

      const stripe = await stripePromise;
      const result = await stripe.verifyIdentity(data.client_secret);

      // Handle the result from Stripe
      if (result.error) {
        // If user canceled or closed the modal, set status to canceled
        if (
          result.error.type === "validation_error" ||
          result.error.code === "user_canceled"
        ) {
          setVerificationStatus("canceled");
        }
      }
    } catch (err) {
      // If there's an error (including user closing the modal), set to canceled
      setVerificationStatus("canceled");
    }
  };

  // Handle verified status - update Redux and navigate
  useEffect(() => {
    if (verificationStatus === "verified") {
      // Update Redux cpa_status to AC (Active/Complete)
      dispatch(updateUserStatus({ account_status: "AC" }));

      // Force a page reload to refresh the route loader data
      window.location.href = "/";
    }
  }, [verificationStatus, dispatch]);

  // WebSocket listener for real-time verification updates using the service
  useEffect(() => {
    const socketService = new IdentityVerificationNotificationService();
    const subscription = socketService.getMessages().subscribe((message) => {
      if (message.status) {
        setVerificationStatus(message.status);
      }
    });

    // Set loading to false after component mounts
    setLoading(false);

    return () => subscription.unsubscribe();
  }, []);

  // Long polling fallback for when webhooks are delayed
  useEffect(() => {
    if (verificationStatus === "processing" && sessionId) {
      const url = "account/check-verification-status";
      const pollInterval = setInterval(async () => {
        try {
          const response = await checkVerificationStatus(url, sessionId);
          const data = await response.json();
          if (data.status && data.status !== "processing") {
            setVerificationStatus(data.status);
            clearInterval(pollInterval);
          }
        } catch (err) {
          console.error("Error checking verification status:", err);
        }
      }, 3000); // Poll every 3 seconds

      // Clear interval after 10 minutes to avoid infinite polling
      const timeout = setTimeout(() => {
        clearInterval(pollInterval);
      }, 600000);

      return () => {
        clearInterval(pollInterval);
        clearTimeout(timeout);
      };
    }
  }, [verificationStatus, sessionId]);

  // Helper function to check if we should show the Verify Now button
  const shouldShowVerifyButton = () => {
    return (
      verificationStatus === null ||
      verificationStatus === "canceled" ||
      verificationStatus === "requires_input"
    );
  };

  // Helper function to check if we should show the full initial content
  const shouldShowFullContent = () => {
    return verificationStatus === null;
  };

  // Helper function to get status message
  const getStatusMessage = () => {
    switch (verificationStatus) {
      case "processing":
        return "Verification in progress...";
      case "requires_input":
        return "Please retry your submission.";
      case "canceled":
        return "Verification canceled.";
      default:
        return null;
    }
  };

  // Redirect if user shouldn't be on this step
  // Legal step is only for status "LE"
  if (cpa_status && cpa_status !== "LE") {
    return <Navigate to="/onboard" replace />;
  }

  return (
    <Fragment>
      <div className="main-content-area">
        <div className="content">
          {loading ? (
            <div className="internal-content-body">
              <LoadingPlaceholder rows={1} type="card-large" />
            </div>
          ) : cpa_status !== "LE" || role !== "CPA" ? (
            <Navigate to="/onboard" replace />
          ) : (
            <Fragment>
              <OnboardCpaSteps />
              <div>
                {shouldShowFullContent() ? (
                  <div>
                    <h1>One quick step to verify your identity.</h1>
                    <br />
                    <p>
                      To keep your account secure and compliant, Plaintr uses
                      Stripe to confirm your identity. This quick step only
                      takes a minute and is required before you can go live.
                    </p>
                    <ol className="bullet-list">
                      <li>Have a valid photo ID ready</li>
                      <li>
                        Stripe may ask you to take a quick selfie for
                        verification
                      </li>
                      <li>
                        All information is encrypted and never shared outside
                        the process
                      </li>
                    </ol>

                    <button
                      className="cta-button primary"
                      data-icon="arrow_forward"
                      onClick={handleVerifyIdentity}
                    >
                      <b>Verify now</b>
                    </button>
                  </div>
                ) : shouldShowVerifyButton() ? (
                  <div>
                    <p className="verification-status">{getStatusMessage()}</p>
                    <button
                      className="cta-button primary"
                      data-icon="arrow_forward"
                      onClick={handleVerifyIdentity}
                    >
                      <b>Verify now</b>
                    </button>
                  </div>
                ) : (
                  <p className="verification-status">{getStatusMessage()}</p>
                )}
              </div>
            </Fragment>
          )}
        </div>
      </div>
    </Fragment>
  );
}

export default OnboardingLegalV2;
